import { gql } from '@apollo/client';
import { CampaignState } from 'constants/campaignStates';
import { GraphQLCampaign } from 'modules/campaigns/lib';
import { useMemo } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';

export const VALID_CAMPAIGN_STATES = [
  CampaignState.Draft,
  CampaignState.Ready,
  CampaignState.Running,
  CampaignState.Scheduled,
];

export const FetchCampaignsByPlacementIdQuery = gql`
  query FetchCampaignsByPlacementId(
    $placementId: Long!
    $campaignStates: [CampaignStateDefinition!]
  ) {
    campaigns(
      campaignFilterInfo: { placementIds: [$placementId], campaignState: $campaignStates }
      pagination: { offset: 0, limit: 1000 }
    ) {
      results {
        id
        name
        templateId
        campaignState
        expectedSendSize
        embeddedPriorityOrder
        messageMedium
        startAt
        schedulerMsgParams {
          endDate
        }
        isJourneyAssociated
        journey: workflow {
          id
          name
          workflowType
          exitRules(includeArchived: false) {
            ... on CustomEventExitRule {
              containsCloningError
            }
            ... on SystemEventExitRule {
              containsCloningError
            }
            ... on UserPropertyExitRule {
              containsCloningError
            }

            ... on ExitRuleCombo {
              containsCloningError
            }
          }
          revisions(offset: 0, limit: 1) {
            results {
              enabledStatus
              createdAt
            }
          }
          allNodes {
            id
            resource {
              ... on CampaignNodeResource {
                campaign {
                  id
                }
              }
            }
          }
        }
        lists {
          id
          name
        }
        displayUntil {
          endDate
          searchQuery {
            ... on SearchCombo {
              combinator
              searchQueries {
                ... on SingleValueSearchField {
                  dataType
                  field
                  comparatorType
                  value
                  dynamicValue
                  fieldType
                  castType
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const useQueryCampaignsByPlacementId = (placementId?: number) => {
  const { data, isLoading, error } = useGraphql(FetchCampaignsByPlacementIdQuery, {
    fetchPolicy: 'network-only',
    skip: !placementId,
    variables: {
      placementId,
      campaignStates: VALID_CAMPAIGN_STATES,
    },
  });

  const campaigns: GraphQLCampaign[] = useMemo(() => data?.campaigns?.results || [], [data]);

  return {
    campaigns,
    loading: isLoading,
    error,
  };
};
